<script setup lang="ts">
  import { Modal } from 'bootstrap'
  import { usePlugin } from '@/store/Pinia/usePlugin.store'

  import { ref, onMounted, computed } from 'vue'

  const clockGif = new URL(
    '../../../assets/images/aguardando.gif',
    import.meta.url,
  ).href
  const clockCompletedImg = new URL(
    '../assets/clock-completed.png',
    import.meta.url,
  ).href

  const modal = ref<InstanceType<typeof Modal> | null>(null)
  const emissionStep = ref(0)
  const interval = ref<any>(null)

  const currentMessageIndex = ref(0)

  const pluginStore = usePlugin()

  const messages = [
    'Aguarde...',
    'Permaneça na página...',
    'Em breve seu certificado será emitido...',
  ]

  const emissionLabel = computed(() => {
    if (emissionStep.value === 1) {
      return 'Gerando chaves...'
    }

    if (emissionStep.value === 2) {
      return 'Emitindo Certificado...'
    }

    if (emissionStep.value === 3) {
      return 'Emissão concluída!'
    }

    return ''
  })

  const message = computed(() => {
    if (emissionStep.value === 3) {
      return 'Certificado emitido!'
    }

    return messages[currentMessageIndex.value]
  })

  const modalImage = computed(() => {
    if (emissionStep.value === 3) {
      return clockCompletedImg
    }
    return clockGif
  })

  const step = computed(() => emissionStep.value)

  onMounted(() => {
    modal.value = new Modal('#modal-emission-steps', {
      backdrop: 'static',
      keyboard: false,
    })
  })

  const canShowSteps = computed(() => {
    if (emissionStep.value === 0) {
      return false
    }

    if (!pluginStore.certificateIssuedAtWasChecked) {
      return false
    }

    return pluginStore.issuedAt?.trim()?.length === 0
  })

  function startCarousel() {
    clearInterval(interval.value)

    interval.value = setInterval(() => {
      if (emissionStep.value === 3) {
        clearInterval(interval.value)
      }

      if (currentMessageIndex.value === messages.length - 1) {
        currentMessageIndex.value = 0
        return
      }

      currentMessageIndex.value += 1
    }, 2500)
  }

  function show() {
    startCarousel()
    modal.value?.show()
  }

  function hide() {
    modal.value?.hide()
  }

  function setEmissionStep(newStep: number) {
    emissionStep.value = newStep
    startCarousel()
  }

  defineExpose({
    show,
    hide,
    setEmissionStep,
    step,
  })
</script>

<template>
  <div
    id="modal-emission-steps"
    class="modal modal-wide fade"
    tabindex="-1"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content px-3 pb-4">
        <div class="modal-body py-2">
          <div class="img-container">
            <img class="clock-img" :src="modalImage" alt="clock-image" />
          </div>

          <div class="pb-4">
            <span>{{ message }}</span>
          </div>

          <div v-if="canShowSteps">
            <div class="loading-bar-background">
              <div
                class="loading-bar"
                :class="{
                  'third-step': emissionStep === 3,
                  'second-step': emissionStep === 2,
                }"
              ></div>
            </div>

            <div class="d-flex justify-content-between mt-2">
              <span>{{ emissionLabel }}</span>
              <span v-if="emissionLabel.length"
                >Passo {{ emissionStep }}/3</span
              >
              <span v-else>&nbsp;</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .img-container {
    display: flex;
    justify-content: center;
    min-height: 140px;
  }

  .clock-img {
    height: 140px;
  }

  .loading-bar-background {
    width: 100%;
    height: 8px;

    background-color: #e6e7ec;
    border-radius: 4px;
  }

  .loading-bar {
    width: 33%;
    height: 8px;

    background-color: #98dd63;
    border-radius: 4px;
  }

  .second-step {
    width: 66%;
  }

  .third-step {
    width: 100%;
  }

  @media (min-width: 576px) {
    .modal-wide {
      --bs-modal-width: 500px;
    }
  }
</style>
